import { useState } from 'react';
import type { InvoiceTemplate } from '@catalyst-psa/shared';
import { INVOICE_TEMPLATE_TOKENS } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { formatDateTime } from '../../utils/date.js';
import { useInvoiceTemplates, useUpdateInvoiceTemplate } from './api.js';

export function InvoiceTemplatesPage(): JSX.Element {
  const templatesQuery = useInvoiceTemplates();
  const updateMutation = useUpdateInvoiceTemplate();

  const [editing, setEditing] = useState<InvoiceTemplate | null>(null);
  const [name, setName] = useState('');
  const [html, setHtml] = useState('');
  const saveMode = useSaveMode();

  const openEditor = (template: InvoiceTemplate): void => {
    setEditing(template);
    setName(template.name);
    setHtml(template.html);
    saveMode.reset();
  };

  const closeEditor = (): void => {
    setEditing(null);
    updateMutation.reset();
  };

  const handleSave = (): void => {
    if (!editing) return;
    updateMutation.mutate(
      { source: editing.source, input: { name: name.trim(), html } },
      {
        onSuccess: () => {
          if (saveMode.closeAfterSaveRef.current) closeEditor();
        },
      },
    );
  };

  const templates = templatesQuery.data ?? [];
  const saveError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? 'Failed to save template.'
        : null;
  const canSave = name.trim().length > 0 && html.trim().length > 0;

  return (
    <div>
      <PageHeader
        title="Invoice Templates"
        description="Edit the PDF layout used for each type of generated invoice."
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <DataState
          isLoading={templatesQuery.isLoading}
          isError={templatesQuery.isError}
          error={templatesQuery.error}
          onRetry={() => void templatesQuery.refetch()}
          isEmpty={!templatesQuery.isLoading && templates.length === 0}
          emptyTitle="No templates"
          emptyDescription="Invoice templates have not been initialised yet."
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3">Template</th>
                <th className="px-5 py-3">Last updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.source} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-900">{template.name}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {formatDateTime(template.updatedAt)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="secondary" onClick={() => openEditor(template)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataState>
      </div>

      <Modal
        isOpen={editing !== null}
        onClose={closeEditor}
        title={editing ? `Edit ${editing.name}` : 'Edit template'}
        size="wide"
        footer={
          <FormActions
            onSubmit={handleSave}
            saveMode={saveMode}
            isPending={updateMutation.isPending}
            disabled={!canSave}
            onCancel={closeEditor}
          />
        }
      >
        <div className="flex flex-col gap-4">
          {saveError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{saveError}</div>
          ) : null}
          <TextField
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextAreaField
            label="HTML template"
            required
            rows={22}
            value={html}
            onChange={(e) => setHtml(e.target.value)}
          />
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <p className="mb-1 font-semibold text-slate-700">Available tokens</p>
            <p className="leading-relaxed">
              Use <code>{'{{token}}'}</code> for escaped text, <code>{'{{{token}}}'}</code> for raw
              HTML (line rows), and <code>{'{{#token}}…{{/token}}'}</code> for conditional sections.
            </p>
            <p className="mt-1 font-mono">{INVOICE_TEMPLATE_TOKENS.join(', ')}</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
